import {useMutation, useQueryClient, type QueryClient} from '@github-ui/react-query'
import {fetchCAPI} from '@github-ui/copilot-shared-ui/capi/fetch-capi'
import {taskApiPath, taskByIdApiPath} from '../utils/api-url-helpers'
import {agentTaskQueryKey} from './use-task'
import {agentTasksQueryKey} from './useTasks'
import type {HydratedAPITask} from '../types/api-task'
import {updateTaskInPage, type TaskListPage} from './repo-task-utils'
import {agentTaskPinsQueryKey} from '../utils/task-pins'

interface UseRenameTaskArgs {
  owner?: string
  repo?: string
  taskId?: string
  apiUrl?: string
}

interface RenameTaskVariables {
  name: string
}

function updateTaskNameInList(queryClient: QueryClient, queryKey: unknown[], taskId: string, name: string) {
  queryClient.setQueriesData<TaskListPage>({queryKey}, oldData => updateTaskInPage(oldData, taskId, {name}))
}

export function useRenameTask({owner, repo, taskId, apiUrl}: UseRenameTaskArgs) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['renameTask', owner, repo, taskId],
    mutationFn: async ({name}: RenameTaskVariables) => {
      if (!taskId) {
        throw new Error('taskId is required')
      }

      const path = owner && repo ? taskApiPath(owner, repo, taskId) : taskByIdApiPath(taskId)
      const response = await fetchCAPI({
        path,
        method: 'PATCH',
        body: {name},
        copilotApiUrl: apiUrl,
      })
      return response.json()
    },
    onSuccess: (_data, variables) => {
      if (!taskId) return

      void queryClient.invalidateQueries({queryKey: agentTaskPinsQueryKey})
      const taskQueryKey = agentTaskQueryKey(owner, repo, taskId)
      queryClient.setQueryData<HydratedAPITask>(taskQueryKey, oldData => {
        if (!oldData) return oldData
        return {...oldData, name: variables.name}
      })

      updateTaskNameInList(queryClient, ['repo-tasks', owner, repo], taskId, variables.name)
      updateTaskNameInList(queryClient, agentTasksQueryKey, taskId, variables.name)

      queryClient.setQueriesData<Record<string, unknown>>({queryKey: ['agent-sessions']}, oldData => {
        if (!oldData || typeof oldData !== 'object') return oldData
        if ('name' in oldData && (oldData as {id?: string}).id === taskId) {
          return {...oldData, name: variables.name}
        }
        return oldData
      })
    },
  })
}
